import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { custodianRolesAPI } from '../../services/custodianRolesAPI';
import api from '../../services/api.js';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../utils/permissionUtils';
import { 
  Shield, 
  Plus, 
  Edit, 
  Trash2, 
  X,
  CheckCircle,
  XCircle,
  UserCheck,
  AlertTriangle,
  Eye
} from 'lucide-react';
import ApproveRequestModal from '../../components/modal/admin/ApproveRequestModal';

const RoleManagement = () => {
  const { user } = useAuth();
  const { isSubAccount } = usePermissions();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [activeTab, setActiveTab] = useState('roles');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedApprovalRequest, setSelectedApprovalRequest] = useState(null);
  const [approvalActionType, setApprovalActionType] = useState('approve');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Form state
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: []
  });

  // Fetch roles
  const { data: rolesData, isLoading: rolesLoading, error: rolesError, refetch: refetchRoles } = useQuery({
    queryKey: ['custodian-roles', debouncedSearchTerm],
    queryFn: () => custodianRolesAPI.getRoles({ search: debouncedSearchTerm }),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000 // 10 minutes
  });

  // Fetch available permissions
  const { data: permissionsData, isLoading: permissionsLoading } = useQuery({
    queryKey: ['custodian-permissions'],
    queryFn: () => custodianRolesAPI.getAvailablePermissions(),
    enabled: !!user
  });

  // Fetch role statistics
  const { data: statsData, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['custodian-role-stats'],
    queryFn: () => custodianRolesAPI.getRoleStats(),
    enabled: !!user,
    select: (response) => {
      const stats = response?.data || {};
      return stats;
    }
  });

  // Fetch pending role approval requests
  const { data: pendingApprovalsData, isLoading: pendingApprovalsLoading } = useQuery({
    queryKey: ['custodian-role-pending-approvals'],
    queryFn: () => custodianRolesAPI.getPendingRoleApprovals(),
    enabled: activeTab === 'pending-approvals' && !!user
  });

  // Fetch role approval history
  const { data: approvalHistoryData, isLoading: approvalHistoryLoading } = useQuery({
    queryKey: ['custodian-role-approval-history'],
    queryFn: () => custodianRolesAPI.getRoleApprovalHistory(),
    enabled: activeTab === 'approval-history' && !!user
  });

  // Get permissions list - flat structure without categories
  const permissions = Array.isArray(permissionsData?.data) ? permissionsData.data : [];

  // Get roles and stats from API
  const roles = rolesData?.data?.roles || [];
  const stats = statsData || { totalRoles: 0, activeRoles: 0, totalAssigned: 0 };


  // Mutations
  const createRoleMutation = useMutation({
    mutationFn: custodianRolesAPI.createRole,
    onSuccess: (data) => {
      // Check if role was created directly or approval was submitted
      if (data.data?.approvalRequest) {
        queryClient.invalidateQueries(['custodian-role-pending-approvals']);
        toast.success('Role creation request submitted for approval');
      } else {
        // Role created directly (first role bypassed approval)
        queryClient.invalidateQueries(['custodian-roles']);
        queryClient.invalidateQueries(['custodian-role-stats']);
      toast.success('Role created successfully');
      }
      setShowCreateModal(false);
      setNewRole({ name: '', description: '', permissions: [] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create role');
    }
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ roleId, updateData }) => custodianRolesAPI.updateRole(roleId, updateData),
    onSuccess: () => {
      queryClient.invalidateQueries(['custodian-roles']);
      queryClient.invalidateQueries(['custodian-role-stats']);
      toast.success('Role updated successfully');
      setShowEditModal(false);
      setSelectedRole(null);
      setNewRole({ name: '', description: '', permissions: [] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update role');
    }
  });

  const deleteRoleMutation = useMutation({
    mutationFn: custodianRolesAPI.deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries(['custodian-roles']);
      queryClient.invalidateQueries(['custodian-role-stats']);
      toast.success('Role deleted successfully');
      setShowDeleteModal(false);
      setSelectedRole(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete role');
    }
  });

  // Role approval mutations
  const approveRoleMutation = useMutation({
    mutationFn: (requestId) => api.put(`/api/users/approval-requests/${requestId}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries(['custodian-role-pending-approvals']);
      queryClient.invalidateQueries(['custodian-role-approval-history']);
      toast.success('Role creation request approved successfully');
      setShowApprovalModal(false);
      setSelectedApprovalRequest(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to approve role creation request');
    }
  });

  const rejectRoleMutation = useMutation({
    mutationFn: ({ requestId, reason }) => api.put(`/api/users/approval-requests/${requestId}/reject`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries(['custodian-role-pending-approvals']);
      queryClient.invalidateQueries(['custodian-role-approval-history']);
      toast.success('Role creation request rejected successfully');
      setShowApprovalModal(false);
      setSelectedApprovalRequest(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reject role creation request');
    }
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const handleCreateRole = () => {
    setNewRole({ name: '', description: '', permissions: [] });
    setShowCreateModal(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setNewRole({
      name: role.name,
      description: role.description,
      permissions: role.permissions || []
    });
    setShowEditModal(true);
  };

  const handleDeleteRole = (role) => {
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  // Role approval handlers
  const handleViewRoleDetails = (request) => {
    setSelectedApprovalRequest(request);
    setShowDetailsModal(true);
  };

  const handleApproveRole = (request) => {
    setSelectedApprovalRequest(request);
    setApprovalActionType('approve');
    setShowApprovalModal(true);
  };

  const handleRejectRole = (request) => {
    setSelectedApprovalRequest(request);
    setApprovalActionType('reject');
    setShowApprovalModal(true);
  };

  const handleApprovalModalApprove = (requestId) => {
    approveRoleMutation.mutate(requestId);
  };

  const handleApprovalModalReject = (requestId, reason) => {
    rejectRoleMutation.mutate({ requestId, reason });
  };

  const handleApprovalModalViewDetails = (request) => {
    setSelectedApprovalRequest(request);
    setShowDetailsModal(true);
  };

  // Check if current user is the initiator of the request
  const isCurrentUserInitiator = (request) => {
    if (!user || !request.initiatorId) return false;
    
    const currentUserId = user.id || user._id;
    const initiatorId = request.initiatorId._id || request.initiatorId;
    
    // For sub-accounts, they can approve requests from their parent account
    // but not their own requests
    if (user.isSubAccount) {
      // If the initiator is the parent account, sub-account can approve
      if (initiatorId === user.parentAccountId) {
        return false; // Not the initiator, can approve
      }
      // If the initiator is the sub-account itself, cannot approve
      return currentUserId === initiatorId;
    }
    
    // For parent accounts, normal logic applies
    return currentUserId === initiatorId;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!newRole.name.trim() || !newRole.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (newRole.permissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }

    if (showEditModal && selectedRole) {
      updateRoleMutation.mutate({
        roleId: selectedRole._id,
        updateData: newRole
      });
    } else {
      createRoleMutation.mutate(newRole);
    }
  };

  const handlePermissionToggle = (permission) => {
    setNewRole(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };


  if (rolesError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading roles</h3>
          <p className="mt-1 text-sm text-gray-500">{rolesError.message}</p>
          <div className="mt-6">
            <button
              onClick={() => refetchRoles()}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Custodian Role Management</h1>
            <p className="text-sm text-gray-500">Create and manage custom roles for sub-accounts</p>
          </div>
          {!isSubAccount && (
            <button
              onClick={handleCreateRole}
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Role
            </button>
          )}
        </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Total Roles */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Roles</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalRoles}</p>
            </div>
          </div>
        </div>

        {/* Active Roles */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Roles</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeRoles}</p>
            </div>
          </div>
        </div>

        {/* Assigned to */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <UserCheck className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Assigned to</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalAssigned}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('roles')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'roles'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Roles
          </button>
          <button
            onClick={() => setActiveTab('pending-approvals')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pending-approvals'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Pending Approvals
          </button>
          <button
            onClick={() => setActiveTab('approval-history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'approval-history'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Approval History
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'roles' && (
        <div className="bg-white rounded-lg   border border-light-gray">
          {/* Search and Actions */}
       

      {/* Roles Table */}
        <div className="overflow-x-auto">
        <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Roles</h3>
          </div>
            <table className="table">
              <thead className="table-header">
              <tr>
                <th className="table-header-cell">
                  Role Name
                </th>
                <th className="table-header-cell">
                    Description
                </th>
                <th className="table-header-cell">
                    Permissions
                </th>
                <th className="table-header-cell">
                    Status
                </th>
                  <th className="table-header-cell">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roles.map((role) => (
                <tr key={role._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{role.name}</div>
                    </td>
                  <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{role.description}</div>
                  </td>
                  <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {role.permissions?.length || 0} permissions
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        role.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {role.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {!isSubAccount && (
                          <button
                            onClick={() => handleEditRole(role)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                        {!isSubAccount && (
                          <button
                            onClick={() => handleDeleteRole(role)}
                            disabled={role.assignedToCount > 0}
                            className={`${
                              role.assignedToCount > 0 
                                ? 'text-gray-400 cursor-not-allowed' 
                                : 'text-error-600 hover:text-error-900'
                            }`}
                            title={role.assignedToCount > 0 
                              ? `Cannot delete: ${role.assignedToCount} sub-account(s) assigned to this role` 
                              : 'Delete role'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                    </div>
                  </td>
                  </tr>
                ))}
              </tbody>
            </table>
                      </div>
                    </div>
      )}

      {/* Pending Approvals Tab */}
      {activeTab === 'pending-approvals' && (
        <div className="bg-white rounded-lg border-light-gray border">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Pending Role Approval Requests</h3>
            <p className="text-sm text-gray-600">Review and approve role creation requests from sub-accounts</p>
          </div>
          
          {pendingApprovalsData?.data?.pendingApprovals?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Role Name</th>
                    <th className="table-header-cell">Initiator</th>
                    <th className="table-header-cell">Submitted</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingApprovalsData.data.pendingApprovals.map((request) => (
                    <tr key={request._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{request.targetData?.name}</div>
                        <div className="text-sm text-gray-500">{request.targetData?.description}</div>
                  </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {request.initiatorId?.email || request.initiatorId?.firstName || 'Unknown'}
                    </div>
                  </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(request.createdAt)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewRoleDetails(request)}
                            className="text-primary-600 hover:text-indigo-900"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {(() => {
                            const isInitiator = isCurrentUserInitiator(request);
                            if (isInitiator) return null;
                            return (
                              <>
                                <button 
                                  onClick={() => handleApproveRole(request)}
                                  className="btn-primary btn-sm"
                                  title="Approve"
                                >
                                  Approve
                                </button>
                                <button 
                                  onClick={() => handleRejectRole(request)}
                                  className="btn-secondary btn-sm"
                                  title="Reject"
                                >
                                  Reject
                                </button>
                              </>
                            );
                          })()}
                        </div>
                      </td>
                </tr>
              ))}
            </tbody>
          </table>
            </div>
          ) : (
            <div className="p-6 text-center">
              <Shield className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No pending approvals</h3>
              <p className="mt-1 text-sm text-gray-500">All role creation requests have been processed.</p>
            </div>
          )}
        </div>
      )}

      {/* Approval History Tab */}
      {activeTab === 'approval-history' && (
        <div className="bg-white rounded-lg border-light-gray border">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Role Approval History</h3>
            <p className="text-sm text-gray-600">Track all role creation request approvals and rejections</p>
      </div>
          
          {approvalHistoryData?.data?.approvalHistory?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      Role Name
                    </th>
                    <th className="table-header-cell">
                      Description
                    </th>
                    <th className="table-header-cell">
                      Status
                    </th>
                    <th className="table-header-cell">
                      Initiated By
                    </th>
                    <th className="table-header-cell">
                      Initiated At
                    </th>
                    <th className="table-header-cell">
                      Approved/Rejected By
                    </th>
                    <th className="table-header-cell">
                      Action Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {approvalHistoryData.data.approvalHistory.map((request) => (
                    <tr key={request._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {request.targetData?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {request.targetData?.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          request.status === 'approved' ? 'bg-green-100 text-green-800' :
                          request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {request.initiatorId?.firstName && request.initiatorId?.lastName ? 
                            `${request.initiatorId.firstName} ${request.initiatorId.lastName}` : 
                            request.initiatorId?.email || 'N/A'
                          }
                        </div>
                        <div className="text-sm text-gray-500">{request.initiatorId?.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">{formatDate(request.createdAt)}</div>
                      </td>
                      <td className="px-6 py-4">
                        {request.status === 'approved' && request.approvedBy ? (
                          <div>
                            <div className="text-sm text-gray-900">
                              {request.approvedBy?.firstName && request.approvedBy?.lastName ? 
                                `${request.approvedBy.firstName} ${request.approvedBy.lastName}` : 
                                request.approvedBy?.email || 'N/A'
                              }
                            </div>
                            <div className="text-sm text-gray-500">{request.approvedBy?.email}</div>
                          </div>
                        ) : request.status === 'rejected' && request.rejectedBy ? (
                          <div>
                            <div className="text-sm text-gray-900">
                              {request.rejectedBy?.firstName && request.rejectedBy?.lastName ? 
                                `${request.rejectedBy.firstName} ${request.rejectedBy.lastName}` : 
                                request.rejectedBy?.email || 'N/A'
                              }
                            </div>
                            <div className="text-sm text-gray-500">{request.rejectedBy?.email}</div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {request.status === 'approved' && request.approvedAt ? formatDate(request.approvedAt) : 
                           request.status === 'rejected' && request.rejectedAt ? formatDate(request.rejectedAt) : 
                           '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-center">
              <Shield className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No approval history</h3>
              <p className="mt-1 text-sm text-gray-500">No role creation requests have been processed yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Role Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {showEditModal ? 'Edit Role' : 'Create New Role'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedRole(null);
                    setNewRole({ name: '', description: '', permissions: [] });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4">
              {/* Basic Information */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role Name *
                  </label>
                  <input
                    type="text"
                    value={newRole.name}
                    onChange={(e) => setNewRole(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter role name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={newRole.description}
                    onChange={(e) => setNewRole(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter role description"
                    rows={3}
                    required
                  />
                </div>
              </div>

              {/* Permissions */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Define Permissions</h4>
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-500">
                      {newRole.permissions.length} of {permissions.length} selected
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => setNewRole(prev => ({ ...prev, permissions: permissions.map(p => p.id) }))}
                        disabled={newRole.permissions.length === permissions.length}
                        className="text-xs text-primary-600 hover:text-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewRole(prev => ({ ...prev, permissions: [] }))}
                        disabled={newRole.permissions.length === 0}
                        className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                </div>

                {permissions.length === 0 ? (
                  <div className="text-center py-8 border border-gray-200 rounded-lg">
                    <div className="text-sm text-gray-500">No permissions available</div>
                  </div>
                ) : (
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                    {permissions.map((permission) => (
                      <div key={permission.id} className="flex items-start p-3 rounded-lg hover:bg-gray-50 border border-gray-100">
                        <input
                          type="checkbox"
                          id={permission.id}
                          checked={newRole.permissions.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                          className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <label htmlFor={permission.id} className="text-sm font-medium text-gray-700 cursor-pointer">
                            {permission.label}
                          </label>
                          {permission.description && (
                            <p className="text-xs text-gray-500 mt-1">{permission.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedRole(null);
                    setNewRole({ name: '', description: '', permissions: [] });
                  }}
                    className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createRoleMutation.isPending || updateRoleMutation.isPending}
                    className="btn-primary"
                >
                  {createRoleMutation.isPending || updateRoleMutation.isPending ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {showEditModal ? 'Updating...' : 'Creating...'}
                    </div>
                  ) : (
                    showEditModal ? 'Update Role' : 'Create Role'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>  
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Delete Role</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete the role "{selectedRole.name}"? This action cannot be undone.
              </p>
              {selectedRole.assignedToCount > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-yellow-400" />
                    <div className="ml-3">
                      <p className="text-sm text-yellow-800">
                        This role is currently assigned to {selectedRole.assignedToCount} sub-account(s). 
                        You must remove the role assignment before deleting it.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteRoleMutation.mutate(selectedRole._id)}
                disabled={deleteRoleMutation.isPending || selectedRole.assignedToCount > 0}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
              >
                {deleteRoleMutation.isPending ? 'Deleting...' : 'Delete Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedApprovalRequest && (
        <ApproveRequestModal
          isOpen={showApprovalModal}
          onClose={() => setShowApprovalModal(false)}
          request={selectedApprovalRequest}
          actionType={approvalActionType}
          onApprove={handleApprovalModalApprove}
          onReject={handleApprovalModalReject}
          onViewDetails={handleApprovalModalViewDetails}
          isLoading={approveRoleMutation.isLoading || rejectRoleMutation.isLoading}
        />
      )}

      {/* Role Details Modal */}
      {showDetailsModal && selectedApprovalRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Role Details</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role Name</label>
                  <div className="mt-1 text-sm text-gray-900">
                    {selectedApprovalRequest.roleData?.name}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <div className="mt-1 text-sm text-gray-900">
                    {selectedApprovalRequest.roleData?.description}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Permissions</label>
                  <div className="mt-1 text-sm text-gray-900">
                    {selectedApprovalRequest.roleData?.permissions?.join(', ') || 'No permissions'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Initiator</label>
                  <div className="mt-1 text-sm text-gray-900">
                    {selectedApprovalRequest.initiatorId?.email || selectedApprovalRequest.initiatorId?.firstName || 'Unknown'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Submitted</label>
                  <div className="mt-1 text-sm text-gray-900">
                    {formatDate(selectedApprovalRequest.createdAt)}
                  </div>
                </div>

                {selectedApprovalRequest.status === 'rejected' && selectedApprovalRequest.rejectionReason && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rejection Reason</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {selectedApprovalRequest.rejectionReason}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default RoleManagement; 